import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Field,
  Input,
  Select,
  Switch,
  Tab,
  TabList,
  Textarea,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from "@fluentui/react-components";
import {
  Add20Regular,
  Alert20Regular,
  ArrowDown16Regular,
  ArrowUp16Regular,
  Checkmark20Regular,
  Delete20Regular,
  Dismiss16Regular,
  Dismiss24Regular,
  Edit20Regular,
  Eye20Regular,
  Lightbulb20Regular,
  Pause20Regular,
  Play20Regular,
  Share20Regular,
  Sparkle20Regular,
  Stop20Regular,
  Target20Regular,
  Timer20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
  Apps20Regular,
  ClipboardTaskListLtr20Regular,
} from "@fluentui/react-icons";
import styles from "./TaskManager.module.css";
import px from "./Taskpersonal.module.css";
import { api, crud, iso, msgOf, parseISO, prioFromApi, utc } from "./Api/Taskapi";
import { Chip, FormDialog, Head, Page, Prio, Tile, Wait, asList, fmtDate, fmtMin, fmtStamp, nice, useAct, useLoad } from "./Taskpages";
import type { PageCtx } from "./Taskpages";

/* ------------------------------------------------------------------ *
 *  Personal productivity: My Day, Lists, Habits, Focus, Priority
 *  matrix and Routines (api/personal/**).
 * ------------------------------------------------------------------ */

const PERS = {
  list: "personal/pers/todolist",
  share: "personal/pers/listshare",
  habit: "personal/pers/habit",
  routine: "personal/pers/routine",
  step: "personal/pers/routinestep",
  session: "personal/pers/focussession",
  score: "personal/pers/taskframeworkscore",
};

/* ---------- shapes ---------- */
interface MineRow {
  id: number;
  workspaceId: number;
  projectId?: number | null;
  projectName?: string | null;
  title: string;
  priority: string;
  dueDate?: string | null;
  isCompleted: boolean;
  estimateMinutes?: number | null;
  quadrant?: string | null;
  personalListId?: number | null;
}
interface HabitDue {
  id: number;
  name: string;
  description?: string | null;
  frequency: string;
  color?: string | null;
  icon?: string | null;
  reminderTime?: string | null;
  currentStreak: number;
  longestStreak: number;
  doneToday: boolean;
  thisWeek: number;
  target: number;
}
interface TodayOut {
  date: string;
  overdue: MineRow[];
  today: MineRow[];
  done: MineRow[];
  habits: HabitDue[];
  totals: { open: number; done: number; estimateMinutes: number };
}
interface WeekOut {
  weekStart: string;
  weekEnd: string;
  days: { date: string; tasks: MineRow[]; count: number; done: number; estimateMinutes: number }[];
}
interface PlanItem {
  id: number;
  taskId: number;
  title: string;
  isCompleted: boolean;
  startTime?: string | null;
  endTime?: string | null;
  durationMinutes?: number | null;
  priority: string;
}
interface PlanOut {
  date: string;
  plan: { id: number; planDate: string; reviewedAt?: string | null; energyLevel?: number | null; note?: string | null; shutdownAt?: string | null } | null;
  items: PlanItem[];
  plannedMinutes: number;
  doneCount: number;
  reviewed: boolean;
  shutDown: boolean;
}
interface DraftItem {
  taskId: number;
  title: string;
  priority: string;
  startTime: string;
  durationMinutes: number;
  reason?: string;
}
interface AutoOut {
  availableMinutes: number;
  plannedMinutes: number;
  leftOut: number;
  items: { taskId: number; title: string; priority: string; startTime: string; durationMinutes: number; reason: string }[];
}
interface ListOut {
  smart: { key: string; count: number }[];
  lists: { id: number; name: string; listType: string; icon?: string | null; color?: string | null; sortOrder: number; parentListId?: number | null; open: number }[];
}

/* ---------- small helpers ---------- */
const today = () => iso(new Date());
const addDaysISO = (s: string, n: number) => {
  const d = parseISO(s);
  d.setDate(d.getDate() + n);
  return iso(d);
};
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const dayLabel = (s: string) => `${DOW[parseISO(s).getDay()]} ${fmtDate(s).slice(0, 6)}`;
const personalWsOf = (ctx: PageCtx) => ctx.me.workspaces.find((w) => w.kind.toUpperCase() === "PERSONAL")?.id ?? null;
const toMin = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};
const toHHMM = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const secs = (n: number) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
const WEEKDAY_CODES = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

/** one task line with a tick box; ticking completes (or re-opens) the task */
const TaskLine: React.FC<{ t: MineRow; ctx: PageCtx; onChanged: () => void; extra?: React.ReactNode }> = ({ t, ctx, onChanged, extra }) => {
  const act = useAct(ctx.notify);
  const late = !t.isCompleted && t.dueDate && t.dueDate.slice(0, 10) < today();
  return (
    <div className={`${px.line} ${t.isCompleted ? px.lineDone : ""}`}>
      <Checkbox
        checked={t.isCompleted}
        aria-label={`${t.isCompleted ? "Re-open" : "Complete"} ${t.title}`}
        onChange={async (_, d) => {
          if (await act(() => api("POST", `/api/tasks/${t.id}/${d.checked === true ? "complete" : "reopen"}`))) onChanged();
        }}
      />
      <button type="button" className={px.lineMain} onClick={() => ctx.openTask(t.id)}>
        <span className={px.lineTitle}>{t.title}</span>
        <span className={px.lineMeta}>
          {t.projectName ? `${t.projectName} · ` : ""}
          {t.dueDate ? <span className={late ? px.late : undefined}>{fmtDate(t.dueDate)}</span> : "No date"}
          {t.estimateMinutes ? ` · ${fmtMin(t.estimateMinutes)}` : ""}
        </span>
      </button>
      {extra}
      {t.priority && t.priority !== "NONE" && <Prio p={prioFromApi(t.priority)} />}
    </div>
  );
};

/** type a task in plain words ("call Ada tomorrow !high"); the backend works out date and priority */
const QuickAdd: React.FC<{ ctx: PageCtx; listId?: number | null; onAdded: () => void; placeholder?: string }> = ({ ctx, listId, onAdded, placeholder }) => {
  const act = useAct(ctx.notify);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ws = personalWsOf(ctx) ?? ctx.workspaceId;
  const add = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    const ok = await act(() => api("POST", "/api/tasks/quick-add", { text: text.trim(), workspaceId: ws, personalListId: listId ?? null }));
    setBusy(false);
    if (ok) {
      setText("");
      onAdded();
    }
  };
  return (
    <div className={px.addRow}>
      <Input
        className={px.grow}
        value={text}
        placeholder={placeholder ?? "Add a task… try “Pay rent friday !high”"}
        aria-label="New task"
        onChange={(_, d) => setText(d.value)}
        onKeyDown={(e) => e.key === "Enter" && void add()}
      />
      <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} disabled={!text.trim() || busy} onClick={() => void add()}>
        Add
      </Button>
    </div>
  );
};

const HabitChip: React.FC<{ h: HabitDue; onToggle: (h: HabitDue) => void }> = ({ h, onToggle }) => (
  <button type="button" className={`${px.habit} ${h.doneToday ? px.habitOn : ""}`} style={{ borderColor: h.color || undefined }} aria-pressed={h.doneToday} onClick={() => onToggle(h)}>
    <span className={px.habitTick}>{h.doneToday ? <Checkmark20Regular /> : null}</span>
    <span className={px.habitName}>{h.name}</span>
    <span className={px.habitMeta}>
      {h.currentStreak} day streak{h.frequency === "WEEKLY" ? ` · ${h.thisWeek}/${h.target} this week` : ""}
    </span>
  </button>
);

/* =================================================================== *
 *  My Day: today, week, inbox and the daily plan
 * =================================================================== */
export const MyDayPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("today");
  return (
    <>
      <Head icon={<WeatherSunny20Regular />} title="My Day" sub="What needs you today, this week, and a plan for the hours in between." />
      <div className={styles.bar}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))}>
          <Tab value="today">Today</Tab>
          <Tab value="week">This week</Tab>
          <Tab value="inbox">Inbox</Tab>
          <Tab value="plan">Plan</Tab>
        </TabList>
      </div>
      {tab === "today" && <TodayTab ctx={ctx} />}
      {tab === "week" && <WeekTab ctx={ctx} />}
      {tab === "inbox" && <InboxTab ctx={ctx} />}
      {tab === "plan" && <PlanTab ctx={ctx} />}
    </>
  );
};

const TodayTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const l = useLoad(() => api<TodayOut>("GET", "/api/personal/today"), []);
  const d = l.data;
  const toggleHabit = async (h: HabitDue) => {
    if (await act(() => api("POST", `/api/personal/habits/${h.id}/check-in`, { toggle: true }))) l.reload();
  };
  const section = (title: string, rows: MineRow[], tone?: string) =>
    rows.length === 0 ? null : (
      <div className={px.card}>
        <h3 className={`${px.cardTitle} ${tone ?? ""}`}>
          {title} <span className={px.count}>{rows.length}</span>
        </h3>
        {rows.map((t) => (
          <TaskLine key={t.id} t={t} ctx={ctx} onChanged={l.reload} />
        ))}
      </div>
    );
  return (
    <Page>
      <QuickAdd ctx={ctx} onAdded={l.reload} />
      <Wait loading={l.loading && !d} error={l.error} onRetry={l.reload} />
      {d && (
        <>
          <div className={px.tiles}>
            <Tile n={d.totals.open} label="Open" />
            <Tile n={d.totals.done} label="Done today" />
            <Tile n={fmtMin(d.totals.estimateMinutes)} label="Estimated work left" />
            <Tile n={d.habits.filter((h) => h.doneToday).length + "/" + d.habits.length} label="Habits" />
          </div>
          {d.habits.length > 0 && (
            <div className={px.card}>
              <h3 className={px.cardTitle}>Habits for today</h3>
              <div className={px.habitRow}>
                {d.habits.map((h) => (
                  <HabitChip key={h.id} h={h} onToggle={(x) => void toggleHabit(x)} />
                ))}
              </div>
            </div>
          )}
          {section("Overdue", d.overdue, px.late)}
          {section("Today", d.today)}
          {section("Done", d.done)}
          {d.overdue.length + d.today.length + d.done.length === 0 && <div className={styles.emptyRow}>Nothing due today. Add a task above or plan your day.</div>}
        </>
      )}
    </Page>
  );
};

const WeekTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [date, setDate] = useState(today());
  const l = useLoad(() => api<WeekOut>("GET", "/api/personal/week", undefined, { date }), [date]);
  const w = l.data;
  const t0 = today();
  return (
    <Page>
      <div className={px.rowBetween}>
        <h3 className={px.cardTitle}>{w ? `${fmtDate(w.weekStart)} – ${fmtDate(w.weekEnd)}` : "This week"}</h3>
        <div className={px.actions}>
          <Button size="small" onClick={() => setDate(addDaysISO(date, -7))}>
            Previous
          </Button>
          <Button size="small" onClick={() => setDate(today())}>
            This week
          </Button>
          <Button size="small" onClick={() => setDate(addDaysISO(date, 7))}>
            Next
          </Button>
        </div>
      </div>
      <Wait loading={l.loading && !w} error={l.error} onRetry={l.reload} />
      {w && (
        <div className={px.weekGrid}>
          {w.days.map((d) => (
            <section key={d.date} className={`${px.dayCol} ${d.date.slice(0, 10) === t0 ? px.dayToday : ""}`} aria-label={dayLabel(d.date)}>
              <header className={px.dayHead}>
                <span>{dayLabel(d.date)}</span>
                <span className={px.count}>
                  {d.done}/{d.count}
                </span>
              </header>
              {d.tasks.map((t) => (
                <TaskLine key={t.id} t={t} ctx={ctx} onChanged={l.reload} />
              ))}
              {d.tasks.length === 0 && <div className={px.muted}>Free</div>}
              {d.estimateMinutes > 0 && <div className={px.muted}>{fmtMin(d.estimateMinutes)} planned</div>}
            </section>
          ))}
        </div>
      )}
    </Page>
  );
};

const InboxTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const l = useLoad(async () => asList<MineRow>(await api("GET", "/api/personal/inbox")), []);
  const lists = useLoad(() => api<ListOut>("GET", "/api/personal/lists"), []);
  const rows = l.data ?? [];
  const own = lists.data?.lists ?? [];
  const patch = async (id: number, body: Record<string, unknown>, ok: string) => {
    if (await act(() => crud.update("tasks/tsk/task", id, body), ok)) {
      l.reload();
      lists.reload();
    }
  };
  return (
    <Page>
      <QuickAdd ctx={ctx} onAdded={() => (l.reload(), lists.reload())} placeholder="Capture something now, sort it later…" />
      <p className={px.hint}>Tasks with no date and no list wait here. Give each one a day or a list and it leaves the inbox.</p>
      <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && rows.length === 0 && "Inbox zero. Nothing to sort."} onRetry={l.reload} />
      {rows.map((t) => (
        <TaskLine
          key={t.id}
          t={t}
          ctx={ctx}
          onChanged={l.reload}
          extra={
            <>
              <Input type="date" size="small" aria-label={`Due date for ${t.title}`} onChange={(_, d) => d.value && void patch(t.id, { dueDate: d.value }, "Date set.")} />
              <Select size="small" aria-label={`List for ${t.title}`} value="" onChange={(_, d) => d.value && void patch(t.id, { personalListId: Number(d.value) }, "Moved to list.")}>
                <option value="">Move to list…</option>
                {own.map((x) => (
                  <option key={x.id} value={String(x.id)}>
                    {x.name}
                  </option>
                ))}
              </Select>
            </>
          }
        />
      ))}
    </Page>
  );
};

const PlanTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [date, setDate] = useState(today());
  const [energy, setEnergy] = useState("3");
  const [available, setAvailable] = useState("480");
  const [start, setStart] = useState("09:00");
  const [note, setNote] = useState("");
  const [draft, setDraft] = useState<DraftItem[] | null>(null);
  const [info, setInfo] = useState("");
  const [pick, setPick] = useState("");
  const [shutOpen, setShutOpen] = useState(false);
  const [roll, setRoll] = useState(true);
  const [moveTo, setMoveTo] = useState("");
  const [shutNote, setShutNote] = useState("");
  const [result, setResult] = useState<{ planned: number; completed: number; notCompleted: number; rescheduled: number; movedTo?: string | null } | null>(null);
  const [busy, setBusy] = useState(false);

  const plan = useLoad(() => api<PlanOut>("GET", "/api/personal/plan", undefined, { date }), [date]);
  const open = useLoad(async () => (draft ? asList<MineRow>(await api("GET", "/api/tasks/my", undefined, { filter: "open", pageSize: 200 })) : []), [draft !== null]);
  const p = plan.data;

  useEffect(() => {
    setDraft(null);
    setResult(null);
    setInfo("");
  }, [date]);
  useEffect(() => {
    if (p?.plan?.energyLevel) setEnergy(String(p.plan.energyLevel));
    setNote(p?.plan?.note ?? "");
  }, [p?.plan?.id, p?.plan?.energyLevel, p?.plan?.note]);

  const suggest = async () => {
    setBusy(true);
    try {
      const r = await api<AutoOut>("POST", "/api/personal/plan/auto", { date, availableMinutes: Number(available) || 480, startTime: start, energyLevel: Number(energy) });
      setDraft(r.items.map((i) => ({ taskId: i.taskId, title: i.title, priority: i.priority, startTime: i.startTime, durationMinutes: i.durationMinutes, reason: i.reason })));
      setInfo(`Suggested ${fmtMin(r.plannedMinutes)} of ${fmtMin(r.availableMinutes)} available${r.leftOut > 0 ? `; ${r.leftOut} more didn’t fit` : ""}.`);
    } catch (e) {
      ctx.notify("error", msgOf(e));
    } finally {
      setBusy(false);
    }
  };
  const edit = () => {
    if (!p) return;
    setDraft(p.items.map((i) => ({ taskId: i.taskId, title: i.title, priority: i.priority, startTime: i.startTime ?? "", durationMinutes: i.durationMinutes ?? 30 })));
    setInfo("");
  };
  const upd = (i: number, patch: Partial<DraftItem>) => setDraft((d) => (d ? d.map((x, j) => (j === i ? { ...x, ...patch } : x)) : d));
  const move = (i: number, by: number) =>
    setDraft((d) => {
      if (!d) return d;
      const j = i + by;
      if (j < 0 || j >= d.length) return d;
      const c = [...d];
      const a = c[i] as DraftItem;
      c[i] = c[j] as DraftItem;
      c[j] = a;
      return c;
    });
  const save = async () => {
    if (!draft) return;
    const ok = await act(
      () =>
        api("POST", "/api/personal/plan/commit", {
          date,
          energyLevel: Number(energy),
          note: note.trim() || null,
          items: draft.map((i) => ({ taskId: i.taskId, startTime: i.startTime || null, durationMinutes: i.durationMinutes })),
        }),
      "Plan saved.",
    );
    if (ok) {
      setDraft(null);
      plan.reload();
    }
  };
  const review = async () => {
    if (await act(() => api("POST", "/api/personal/plan/review", { date, energyLevel: Number(energy), note: note.trim() || null }), "Morning review done.")) plan.reload();
  };
  const shutdown = async () => {
    try {
      const r = await api<{ planned: number; completed: number; notCompleted: number; rescheduled: number; movedTo?: string | null }>("POST", "/api/personal/plan/shutdown", {
        date,
        note: shutNote.trim() || null,
        rollOver: roll,
        moveTo: roll && moveTo ? moveTo : null,
      });
      setResult(r);
      setShutOpen(false);
      plan.reload();
    } catch (e) {
      ctx.notify("error", msgOf(e));
    }
  };

  const total = (draft ?? []).reduce((a, i) => a + (i.durationMinutes || 0), 0);
  const addable = (open.data ?? []).filter((t) => !(draft ?? []).some((d) => d.taskId === t.id));
  const REASON: Record<string, string> = { OVERDUE: "Overdue", DUE_TODAY: "Due today", PRIORITY: "High priority" };

  return (
    <Page>
      <div className={px.card}>
        <div className={px.rowBetween}>
          <h3 className={px.cardTitle}>Plan for {fmtDate(date)}</h3>
          <div className={px.actions}>
            <Button size="small" onClick={() => setDate(addDaysISO(date, -1))}>
              Previous
            </Button>
            <Input type="date" size="small" aria-label="Plan date" value={date} onChange={(_, d) => d.value && setDate(d.value)} />
            <Button size="small" onClick={() => setDate(addDaysISO(date, 1))}>
              Next
            </Button>
          </div>
        </div>
        <div className={px.formGrid}>
          <Field label={`Energy today: ${energy}/5`}>
            <input type="range" min={1} max={5} step={1} value={energy} onChange={(e) => setEnergy(e.target.value)} className={px.range} aria-label="Energy level" />
          </Field>
          <Field label="Time available (minutes)">
            <Input type="number" min="30" max="960" value={available} onChange={(_, d) => setAvailable(d.value)} />
          </Field>
          <Field label="Start at">
            <Input type="time" value={start} onChange={(_, d) => setStart(d.value || "09:00")} />
          </Field>
        </div>
        <Field label="Note for the day">
          <Textarea value={note} onChange={(_, d) => setNote(d.value)} rows={2} resize="vertical" />
        </Field>
        <div className={px.actions}>
          <Button className={styles.primary} appearance="primary" icon={<Sparkle20Regular />} disabled={busy} onClick={() => void suggest()}>
            Suggest a plan
          </Button>
          <Button icon={<Edit20Regular />} disabled={!p || p.items.length === 0} onClick={edit}>
            Edit saved plan
          </Button>
          <Button icon={<Eye20Regular />} onClick={() => void review()}>
            Morning review
          </Button>
          <Button icon={<WeatherMoon20Regular />} disabled={!p?.plan} onClick={() => setShutOpen(true)}>
            Shut down the day
          </Button>
        </div>
      </div>

      {result && (
        <div className={px.card}>
          <h3 className={px.cardTitle}>Day closed</h3>
          <div className={px.tiles}>
            <Tile n={result.planned} label="Planned" />
            <Tile n={result.completed} label="Finished" />
            <Tile n={result.notCompleted} label="Not finished" />
            <Tile n={result.rescheduled} label={result.movedTo ? `Moved to ${fmtDate(result.movedTo)}` : "Rescheduled"} />
          </div>
        </div>
      )}

      {draft && (
        <div className={px.card}>
          <div className={px.rowBetween}>
            <h3 className={px.cardTitle}>
              Draft plan · {fmtMin(total)}
              {Number(available) > 0 && total > Number(available) ? " (over your available time)" : ""}
            </h3>
            <div className={px.actions}>
              <Button className={styles.primary} appearance="primary" icon={<Checkmark20Regular />} onClick={() => void save()}>
                Save plan
              </Button>
              <Button onClick={() => setDraft(null)}>Discard</Button>
            </div>
          </div>
          {info && <p className={px.hint}>{info}</p>}
          {draft.length === 0 && <div className={styles.emptyRow}>The plan is empty. Add a task below.</div>}
          {draft.map((i, idx) => (
            <div key={i.taskId} className={px.planRow}>
              <div className={px.planMove}>
                <Button size="small" appearance="subtle" icon={<ArrowUp16Regular />} aria-label="Move up" disabled={idx === 0} onClick={() => move(idx, -1)} />
                <Button size="small" appearance="subtle" icon={<ArrowDown16Regular />} aria-label="Move down" disabled={idx === draft.length - 1} onClick={() => move(idx, 1)} />
              </div>
              <div className={px.grow}>
                <div className={px.lineTitle}>{i.title}</div>
                {i.reason && <span className={px.lineMeta}>{REASON[i.reason] ?? nice(i.reason)}</span>}
              </div>
              <Input type="time" size="small" aria-label={`Start time for ${i.title}`} value={i.startTime} onChange={(_, d) => upd(idx, { startTime: d.value })} />
              <Input type="number" size="small" min="5" max="480" aria-label={`Minutes for ${i.title}`} value={String(i.durationMinutes)} onChange={(_, d) => upd(idx, { durationMinutes: Math.max(5, Number(d.value) || 5) })} />
              <Button size="small" appearance="subtle" icon={<Dismiss16Regular />} aria-label={`Remove ${i.title}`} onClick={() => setDraft(draft.filter((_, j) => j !== idx))} />
            </div>
          ))}
          <div className={px.addRow}>
            <Select
              className={px.grow}
              aria-label="Add a task to the plan"
              value={pick}
              onChange={(_, d) => {
                const t = addable.find((x) => String(x.id) === d.value);
                setPick("");
                if (t) {
                  const last = draft[draft.length - 1];
                  const startAt = last && last.startTime ? toHHMM(toMin(last.startTime) + last.durationMinutes + 5) : start;
                  setDraft([...draft, { taskId: t.id, title: t.title, priority: t.priority, startTime: startAt, durationMinutes: t.estimateMinutes && t.estimateMinutes > 0 ? t.estimateMinutes : 30 }]);
                }
              }}
            >
              <option value="">Add one of your open tasks…</option>
              {addable.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.title}
                </option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {!draft && (
        <div className={px.card}>
          <h3 className={px.cardTitle}>Saved plan</h3>
          <Wait loading={plan.loading && !p} error={plan.error} onRetry={plan.reload} />
          {p && (
            <>
              <div className={px.tiles}>
                <Tile n={fmtMin(p.plannedMinutes)} label="Planned" />
                <Tile n={`${p.doneCount}/${p.items.length}`} label="Done" />
                <Tile n={p.reviewed ? "Yes" : "No"} label="Reviewed" />
                <Tile n={p.shutDown ? "Yes" : "No"} label="Day closed" />
              </div>
              {p.items.length === 0 && <div className={styles.emptyRow}>No plan saved for this day. Press “Suggest a plan”.</div>}
              {p.items.map((i) => (
                <div key={i.id} className={`${px.planRow} ${i.isCompleted ? px.lineDone : ""}`}>
                  <span className={px.time}>{i.startTime ? `${i.startTime}${i.endTime ? `–${i.endTime}` : ""}` : "Anytime"}</span>
                  <button type="button" className={px.lineMain} onClick={() => ctx.openTask(i.taskId)}>
                    <span className={px.lineTitle}>{i.title}</span>
                    <span className={px.lineMeta}>{fmtMin(i.durationMinutes ?? 30)}</span>
                  </button>
                  {i.isCompleted ? <Chip text="Done" /> : i.priority !== "NONE" ? <Prio p={prioFromApi(i.priority)} /> : null}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      <FormDialog open={shutOpen} title="Shut down the day" action="Close the day" onClose={() => setShutOpen(false)} onSubmit={() => void shutdown()}>
        <Switch checked={roll} onChange={(_, d) => setRoll(d.checked)} label="Move unfinished planned tasks to another day" />
        {roll && (
          <Field label="Move them to" hint="Leave empty for tomorrow.">
            <Input type="date" min={addDaysISO(date, 1)} value={moveTo} onChange={(_, d) => setMoveTo(d.value)} />
          </Field>
        )}
        <Field label="Closing note">
          <Textarea value={shutNote} onChange={(_, d) => setShutNote(d.value)} rows={2} resize="vertical" />
        </Field>
      </FormDialog>
    </Page>
  );
};

/* =================================================================== *
 *  Lists
 * =================================================================== */
interface SharedList {
  id: number;
  ownerUserId: number;
  ownerName?: string | null;
  name: string;
  permission: string;
}
interface ShareRow {
  id: number;
  listId: number;
  sharedWithUserId: number;
  permission: string;
}
type Sel = { kind: "smart"; key: string } | { kind: "own"; id: number } | { kind: "shared"; id: number };
const SMART_LABEL: Record<string, string> = { INBOX: "Inbox", TODAY: "Today", UPCOMING: "Upcoming", ANYTIME: "Anytime", URGENT: "Urgent" };
const LIST_COLORS = ["#2ea043", "#0f6cbd", "#f2994a", "#e5484d", "#8764b8", "#038387"];

export const ListsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [sel, setSel] = useState<Sel>({ kind: "smart", key: "TODAY" });
  const [includeDone, setIncludeDone] = useState(false);
  const [nameOpen, setNameOpen] = useState<null | "new" | "rename">(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState(LIST_COLORS[1] as string);
  const [shareOpen, setShareOpen] = useState(false);
  const [dragFrom, setDragFrom] = useState<number | null>(null);

  const lists = useLoad(() => api<ListOut>("GET", "/api/personal/lists"), []);
  const shared = useLoad(async () => asList<SharedList>(await api("GET", "/api/personal/lists/shared-with-me")), []);
  const tasks = useLoad(async (): Promise<MineRow[]> => {
    if (sel.kind === "own") return asList<MineRow>(await api("GET", `/api/personal/lists/${sel.id}/tasks`, undefined, { includeDone }));
    if (sel.kind === "shared") return asList<MineRow>(await api("GET", `/api/personal/lists/shared/${sel.id}/tasks`));
    if (sel.key === "INBOX") return asList<MineRow>(await api("GET", "/api/personal/inbox"));
    if (sel.key === "TODAY") {
      const t = await api<TodayOut>("GET", "/api/personal/today");
      return [...t.overdue, ...t.today, ...(includeDone ? t.done : [])];
    }
    if (sel.key === "UPCOMING") return asList<MineRow>(await api("GET", "/api/tasks/my", undefined, { filter: "upcoming", pageSize: 200 }));
    if (sel.key === "ANYTIME") return asList<MineRow>(await api("GET", "/api/tasks/my", undefined, { filter: "no-date", pageSize: 200 }));
    return asList<MineRow>(await api("GET", "/api/tasks/my", undefined, { filter: "open", pageSize: 500 })).filter((t) => ["HIGH", "URGENT"].includes(t.priority.toUpperCase()));
  }, [sel.kind, sel.kind === "smart" ? sel.key : (sel as { id: number }).id, includeDone]);

  const own = lists.data?.lists ?? [];
  const current = sel.kind === "own" ? own.find((l) => l.id === sel.id) : null;
  const sharedCur = sel.kind === "shared" ? (shared.data ?? []).find((l) => l.id === sel.id) : null;
  const rows = tasks.data ?? [];
  const title = sel.kind === "smart" ? (SMART_LABEL[sel.key] ?? nice(sel.key)) : (current?.name ?? sharedCur?.name ?? "List");
  const reloadAll = () => {
    lists.reload();
    tasks.reload();
  };

  const saveName = async () => {
    if (!name.trim()) return;
    if (nameOpen === "new") {
      try {
        const c = await crud.create<{ id: number }>(PERS.list, { name: name.trim(), listType: "CUSTOM", color, sortOrder: (own[own.length - 1]?.sortOrder ?? 0) + 10 });
        setNameOpen(null);
        lists.reload();
        setSel({ kind: "own", id: c.id });
        ctx.notify("success", "List created.");
      } catch (e) {
        ctx.notify("error", msgOf(e));
      }
    } else if (current) {
      if (await act(() => crud.update(PERS.list, current.id, { name: name.trim(), color }), "List renamed.")) {
        setNameOpen(null);
        lists.reload();
      }
    }
  };

  const reorder = async (from: number, to: number) => {
    if (sel.kind !== "own" || from === to) return;
    const ids = rows.map((r) => r.id);
    const [m] = ids.splice(from, 1);
    ids.splice(to, 0, m as number);
    tasks.setData(ids.map((id) => rows.find((r) => r.id === id) as MineRow));
    if (!(await act(() => api("POST", `/api/personal/lists/${sel.id}/reorder`, { taskIds: ids })))) tasks.reload();
  };

  return (
    <>
      <Head icon={<ClipboardTaskListLtr20Regular />} title="Lists" sub="Your own lists, smart views of your tasks, and lists shared with you.">
        <Button
          className={styles.primary}
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => {
            setName("");
            setColor(LIST_COLORS[1] as string);
            setNameOpen("new");
          }}
        >
          New list
        </Button>
      </Head>
      <div className={styles.body}>
        <div className={px.split}>
          <nav className={px.splitSide} aria-label="Lists">
            <div className={px.sideHead}>Smart lists</div>
            {(lists.data?.smart ?? []).map((s) => (
              <button key={s.key} type="button" className={`${px.sideItem} ${sel.kind === "smart" && sel.key === s.key ? px.sideOn : ""}`} onClick={() => setSel({ kind: "smart", key: s.key })}>
                <span className={px.grow}>{SMART_LABEL[s.key] ?? nice(s.key)}</span>
                <span className={px.count}>{s.count}</span>
              </button>
            ))}
            <div className={px.sideHead}>My lists</div>
            {own.map((l) => (
              <button key={l.id} type="button" className={`${px.sideItem} ${sel.kind === "own" && sel.id === l.id ? px.sideOn : ""}`} onClick={() => setSel({ kind: "own", id: l.id })}>
                <span className={px.dot} style={{ background: l.color || "#0f6cbd" }} aria-hidden="true" />
                <span className={px.grow}>{l.name}</span>
                <span className={px.count}>{l.open}</span>
              </button>
            ))}
            {!lists.loading && own.length === 0 && <div className={px.muted}>No lists yet</div>}
            <div className={px.sideHead}>Shared with me</div>
            {(shared.data ?? []).map((l) => (
              <button key={l.id} type="button" className={`${px.sideItem} ${sel.kind === "shared" && sel.id === l.id ? px.sideOn : ""}`} onClick={() => setSel({ kind: "shared", id: l.id })}>
                <Share20Regular />
                <span className={px.grow}>
                  {l.name}
                  <span className={px.muted}> · {l.ownerName ?? "someone"}</span>
                </span>
              </button>
            ))}
            {!shared.loading && (shared.data ?? []).length === 0 && <div className={px.muted}>Nothing shared with you</div>}
          </nav>
          <section className={px.splitMain} aria-label={title}>
            <div className={px.rowBetween}>
              <h2 className={px.docTitle}>
                {title}
                {sharedCur && <span className={px.muted}> · {nice(sharedCur.permission)} access</span>}
              </h2>
              <div className={px.actions}>
                {sel.kind !== "shared" && sel.kind !== "smart" ? null : null}
                {(sel.kind === "own" || (sel.kind === "smart" && (sel.key === "TODAY"))) && <Switch checked={includeDone} onChange={(_, d) => setIncludeDone(d.checked)} label="Show done" />}
                {current && (
                  <>
                    <Button icon={<Share20Regular />} onClick={() => setShareOpen(true)}>
                      Share
                    </Button>
                    <Button
                      icon={<Edit20Regular />}
                      onClick={() => {
                        setName(current.name);
                        setColor(current.color || (LIST_COLORS[1] as string));
                        setNameOpen("rename");
                      }}
                    >
                      Rename
                    </Button>
                    <Button
                      icon={<Delete20Regular />}
                      disabled={current.listType === "INBOX"}
                      onClick={async () => {
                        if (await act(() => crud.remove(PERS.list, current.id), "List deleted.")) {
                          setSel({ kind: "smart", key: "TODAY" });
                          lists.reload();
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
            {sel.kind !== "shared" && <QuickAdd ctx={ctx} listId={sel.kind === "own" ? sel.id : null} onAdded={reloadAll} />}
            <Wait loading={tasks.loading && !tasks.data} error={tasks.error} empty={!tasks.loading && rows.length === 0 && "Nothing here"} onRetry={tasks.reload} />
            {rows.map((t, i) =>
              sel.kind === "shared" ? (
                <div key={t.id} className={`${px.line} ${t.isCompleted ? px.lineDone : ""}`}>
                  <button type="button" className={px.lineMain} onClick={() => ctx.openTask(t.id)}>
                    <span className={px.lineTitle}>{t.isCompleted ? "✓ " : ""}{t.title}</span>
                    <span className={px.lineMeta}>{t.dueDate ? fmtDate(t.dueDate) : "No date"}</span>
                  </button>
                  {t.priority !== "NONE" && <Prio p={prioFromApi(t.priority)} />}
                </div>
              ) : (
                <div
                  key={t.id}
                  draggable={sel.kind === "own"}
                  onDragStart={() => setDragFrom(i)}
                  onDragOver={(e) => sel.kind === "own" && e.preventDefault()}
                  onDrop={() => {
                    if (dragFrom !== null) void reorder(dragFrom, i);
                    setDragFrom(null);
                  }}
                  className={dragFrom === i ? px.dragging : undefined}
                >
                  <TaskLine
                    t={t}
                    ctx={ctx}
                    onChanged={reloadAll}
                    extra={
                      sel.kind === "own" ? (
                        <span className={px.planMove}>
                          <Button size="small" appearance="subtle" icon={<ArrowUp16Regular />} aria-label={`Move ${t.title} up`} disabled={i === 0} onClick={() => void reorder(i, i - 1)} />
                          <Button size="small" appearance="subtle" icon={<ArrowDown16Regular />} aria-label={`Move ${t.title} down`} disabled={i === rows.length - 1} onClick={() => void reorder(i, i + 1)} />
                        </span>
                      ) : undefined
                    }
                  />
                </div>
              ),
            )}
          </section>
        </div>
      </div>

      <FormDialog open={nameOpen !== null} title={nameOpen === "new" ? "New list" : "Rename list"} action={nameOpen === "new" ? "Create list" : "Save"} disabled={!name.trim()} onClose={() => setNameOpen(null)} onSubmit={() => void saveName()}>
        <Field label="Name" required>
          <Input value={name} onChange={(_, d) => setName(d.value)} placeholder="Groceries" />
        </Field>
        <Field label="Colour">
          <div className={px.swatches} role="radiogroup" aria-label="Colour">
            {LIST_COLORS.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={color === c} aria-label={c} className={`${px.swatch} ${color === c ? px.swatchOn : ""}`} style={{ background: c }} onClick={() => setColor(c)} />
            ))}
          </div>
        </Field>
      </FormDialog>

      {current && <ShareDialog open={shareOpen} listId={current.id} ctx={ctx} onClose={() => setShareOpen(false)} />}
    </>
  );
};

const ShareDialog: React.FC<{ open: boolean; listId: number; ctx: PageCtx; onClose: () => void }> = ({ open, listId, ctx, onClose }) => {
  const act = useAct(ctx.notify);
  const [who, setWho] = useState("");
  const [perm, setPerm] = useState("VIEW");
  const shares = useLoad(async () => (open ? (await crud.list<ShareRow>(PERS.share, { listId })).items : []), [open, listId]);
  const rows = shares.data ?? [];
  const nameOf = (id: number) => ctx.people.find((p) => p.id === id)?.name ?? `User ${id}`;
  const options = ctx.people.filter((p) => p.id !== ctx.me.id && !rows.some((r) => r.sharedWithUserId === p.id));
  return (
    <Dialog open={open} onOpenChange={(_, o) => !o.open && onClose()}>
      <DialogSurface className={styles.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Share this list
          </DialogTitle>
          <DialogContent className={styles.formBody}>
            <Wait loading={shares.loading && !shares.data} error={shares.error} empty={!shares.loading && rows.length === 0 && "Not shared with anyone yet"} />
            {rows.map((r) => (
              <div key={r.id} className={px.line}>
                <span className={px.lineMain}>
                  <span className={px.lineTitle}>{nameOf(r.sharedWithUserId)}</span>
                  <span className={px.lineMeta}>{nice(r.permission)} access</span>
                </span>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  aria-label={`Stop sharing with ${nameOf(r.sharedWithUserId)}`}
                  onClick={async () => {
                    if (await act(() => crud.remove(PERS.share, r.id))) shares.reload();
                  }}
                />
              </div>
            ))}
            <div className={px.addRow}>
              <Select className={px.grow} aria-label="Person" value={who} onChange={(_, d) => setWho(d.value)}>
                <option value="">Choose a person…</option>
                {options.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Select aria-label="Access" value={perm} onChange={(_, d) => setPerm(d.value)}>
                <option value="VIEW">Can view</option>
                <option value="EDIT">Can edit</option>
              </Select>
              <Button
                className={styles.primary}
                appearance="primary"
                disabled={!who}
                onClick={async () => {
                  const ok = await act(async () => {
                    await crud.create(PERS.share, { listId, sharedWithUserId: Number(who), permission: perm });
                    await crud.update(PERS.list, listId, { isShared: true });
                  }, "Shared.");
                  if (ok) {
                    setWho("");
                    shares.reload();
                  }
                }}
              >
                Share
              </Button>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* =================================================================== *
 *  Habits
 * =================================================================== */
interface HabitRec {
  id: number;
  name: string;
  description?: string | null;
  frequency: string;
  frequencyConfig?: string | null;
  targetPerPeriod: number;
  startDate?: string | null;
  endDate?: string | null;
  reminderTime?: string | null;
  color?: string | null;
  isArchived: boolean;
  currentStreak: number;
  longestStreak: number;
  lastCheckInDate?: string | null;
}
interface HabitStats {
  id: number;
  name: string;
  frequency: string;
  currentStreak: number;
  longestStreak: number;
  days: number;
  checkIns: number;
  total: number;
  completionRate: number;
  bestWeekday?: string | null;
  heatmap: { date: string; done: boolean }[];
}
interface HabitForm {
  name: string;
  description: string;
  frequency: string;
  days: string[];
  target: string;
  start: string;
  end: string;
  reminder: string;
  color: string;
}
const blankHabit = (): HabitForm => ({ name: "", description: "", frequency: "DAILY", days: ["MON", "WED", "FRI"], target: "3", start: "", end: "", reminder: "", color: LIST_COLORS[0] as string });

const Heatmap: React.FC<{ cells: { date: string; done: boolean }[]; color: string; onPick: (date: string) => void }> = ({ cells, color, onPick }) => {
  if (cells.length === 0) return null;
  const S = 14;
  const G = 3;
  const first = parseISO((cells[0] as { date: string }).date);
  const offset = (first.getDay() + 6) % 7;
  const weeks = Math.ceil((cells.length + offset) / 7);
  return (
    <div className={px.scroll}>
      <svg width={weeks * (S + G) + 4} height={7 * (S + G) + 4} role="group" aria-label="Check-ins by day. Select a day to add or remove it.">
        {cells.map((c, i) => {
          const k = i + offset;
          const x = Math.floor(k / 7) * (S + G) + 2;
          const y = (k % 7) * (S + G) + 2;
          return (
            <rect
              key={c.date}
              x={x}
              y={y}
              width={S}
              height={S}
              rx={3}
              fill={c.done ? color : "#ebebeb"}
              tabIndex={0}
              role="button"
              aria-label={`${fmtDate(c.date)}: ${c.done ? "done" : "not done"}`}
              style={{ cursor: "pointer" }}
              onClick={() => onPick(c.date)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPick(c.date)}
            >
              <title>{`${fmtDate(c.date)} · ${c.done ? "done" : "not done"}`}</title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
};

export const HabitsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [tab, setTab] = useState("today");
  const [edit, setEdit] = useState<{ id: number | null; f: HabitForm } | null>(null);
  const [logFor, setLogFor] = useState<HabitRec | null>(null);
  const [log, setLog] = useState({ date: today(), value: "1", note: "" });
  const [statId, setStatId] = useState<number | null>(null);
  const [statDays, setStatDays] = useState("90");

  const due = useLoad(async () => asList<HabitDue>(await api("GET", "/api/personal/habits/today")), []);
  const all = useLoad(async () => (await crud.list<HabitRec>(PERS.habit, { sort: "name" })).items, []);
  const stats = useLoad(() => (statId === null ? Promise.resolve(null) : api<HabitStats>("GET", `/api/personal/habits/${statId}/stats`, undefined, { days: Number(statDays) })), [statId, statDays]);
  const habits = all.data ?? [];
  const reloadAll = () => {
    due.reload();
    all.reload();
    stats.reload();
  };
  const chosen = habits.find((h) => h.id === statId);

  const openEdit = (h: HabitRec | null) =>
    setEdit({
      id: h?.id ?? null,
      f: h
        ? {
            name: h.name,
            description: h.description ?? "",
            frequency: h.frequency,
            days: (h.frequencyConfig ?? "").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean),
            target: String(h.targetPerPeriod),
            start: h.startDate?.slice(0, 10) ?? "",
            end: h.endDate?.slice(0, 10) ?? "",
            reminder: h.reminderTime ?? "",
            color: h.color || (LIST_COLORS[0] as string),
          }
        : blankHabit(),
    });
  const saveHabit = async () => {
    if (!edit) return;
    const f = edit.f;
    const body: Record<string, unknown> = {
      name: f.name.trim(),
      description: f.description.trim() || null,
      frequency: f.frequency,
      frequencyConfig: f.frequency === "DAYS" ? f.days.join(",") : null,
      targetPerPeriod: f.frequency === "WEEKLY" ? Math.max(1, Number(f.target) || 1) : 1,
      startDate: f.start || null,
      endDate: f.end || null,
      reminderTime: f.reminder || null,
      color: f.color,
    };
    const ok = await act(() => (edit.id === null ? crud.create(PERS.habit, body) : crud.update(PERS.habit, edit.id, body)), edit.id === null ? "Habit created." : "Habit saved.");
    if (ok) {
      setEdit(null);
      reloadAll();
    }
  };

  return (
    <>
      <Head icon={<Target20Regular />} title="Habits" sub="Small things you repeat, with streaks to keep you honest.">
        <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => openEdit(null)}>
          New habit
        </Button>
      </Head>
      <div className={styles.bar}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))}>
          <Tab value="today">Today</Tab>
          <Tab value="all">All habits</Tab>
        </TabList>
      </div>
      <Page>
        {tab === "today" && (
          <>
            <Wait loading={due.loading && !due.data} error={due.error} empty={!due.loading && (due.data ?? []).length === 0 && "No habits due today. Create one to get started."} onRetry={due.reload} />
            <div className={px.habitRow}>
              {(due.data ?? []).map((h) => (
                <HabitChip
                  key={h.id}
                  h={h}
                  onToggle={async (x) => {
                    if (await act(() => api("POST", `/api/personal/habits/${x.id}/check-in`, { toggle: true }))) reloadAll();
                  }}
                />
              ))}
            </div>
          </>
        )}
        {tab === "all" && (
          <>
            <Wait loading={all.loading && !all.data} error={all.error} empty={!all.loading && habits.length === 0 && "No habits yet"} onRetry={all.reload} />
            {habits.length > 0 && (
              <div className={px.scroll}>
                <table className={px.tbl}>
                  <thead>
                    <tr>
                      <th>Habit</th>
                      <th>Repeats</th>
                      <th>Streak</th>
                      <th>Best</th>
                      <th>Last check-in</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {habits.map((h) => (
                      <tr key={h.id} className={h.isArchived ? px.faded : undefined}>
                        <td className={px.strong}>
                          <span className={px.dot} style={{ background: h.color || "#0f6cbd" }} aria-hidden="true" /> {h.name} {h.isArchived && <Chip text="Archived" />}
                        </td>
                        <td>{h.frequency === "DAYS" ? (h.frequencyConfig ?? "").replace(/,/g, ", ") : h.frequency === "WEEKLY" ? `${h.targetPerPeriod}× a week` : "Every day"}</td>
                        <td>{h.currentStreak}</td>
                        <td>{h.longestStreak}</td>
                        <td>{h.lastCheckInDate ? fmtDate(h.lastCheckInDate) : "—"}</td>
                        <td className={px.actions}>
                          <Button size="small" onClick={() => setStatId(h.id)}>
                            Stats
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setLog({ date: today(), value: "1", note: "" });
                              setLogFor(h);
                            }}
                          >
                            Log
                          </Button>
                          <Button size="small" appearance="subtle" icon={<Edit20Regular />} aria-label={`Edit ${h.name}`} onClick={() => openEdit(h)} />
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<Delete20Regular />}
                            aria-label={`Delete ${h.name}`}
                            onClick={async () => {
                              if (await act(() => crud.remove(PERS.habit, h.id), "Habit deleted.")) {
                                if (statId === h.id) setStatId(null);
                                reloadAll();
                              }
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {statId !== null && (
              <div className={px.card}>
                <div className={px.rowBetween}>
                  <h3 className={px.cardTitle}>{chosen?.name ?? stats.data?.name ?? "Habit"} · statistics</h3>
                  <div className={px.actions}>
                    <Select aria-label="Period" value={statDays} onChange={(_, d) => setStatDays(d.value)}>
                      {["30", "90", "180", "365"].map((d) => (
                        <option key={d} value={d}>
                          Last {d} days
                        </option>
                      ))}
                    </Select>
                    <Button onClick={() => setStatId(null)}>Close</Button>
                  </div>
                </div>
                <Wait loading={stats.loading && !stats.data} error={stats.error} onRetry={stats.reload} />
                {stats.data && (
                  <>
                    <div className={px.tiles}>
                      <Tile n={stats.data.currentStreak} label="Current streak" />
                      <Tile n={stats.data.longestStreak} label="Longest streak" />
                      <Tile n={`${stats.data.completionRate}%`} label="Completion" />
                      <Tile n={stats.data.checkIns} label="Check-ins" />
                      <Tile n={stats.data.bestWeekday ?? "—"} label="Best day" />
                    </div>
                    <Heatmap
                      cells={stats.data.heatmap}
                      color={chosen?.color || (LIST_COLORS[0] as string)}
                      onPick={async (date) => {
                        if (await act(() => api("POST", `/api/personal/habits/${stats.data?.id}/check-in`, { date, toggle: true }))) reloadAll();
                      }}
                    />
                    <p className={px.hint}>Select a day to add or remove a check-in.</p>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </Page>

      <FormDialog open={edit !== null} title={edit?.id === null ? "New habit" : "Edit habit"} action={edit?.id === null ? "Create habit" : "Save"} disabled={!edit?.f.name.trim() || (edit?.f.frequency === "DAYS" && edit.f.days.length === 0)} onClose={() => setEdit(null)} onSubmit={() => void saveHabit()}>
        {edit && (
          <>
            <Field label="Name" required>
              <Input value={edit.f.name} onChange={(_, d) => setEdit({ ...edit, f: { ...edit.f, name: d.value } })} placeholder="Read for 20 minutes" />
            </Field>
            <Field label="Description">
              <Input value={edit.f.description} onChange={(_, d) => setEdit({ ...edit, f: { ...edit.f, description: d.value } })} />
            </Field>
            <div className={styles.formRow}>
              <Field label="Repeats" className={styles.grow}>
                <Select value={edit.f.frequency} onChange={(_, d) => setEdit({ ...edit, f: { ...edit.f, frequency: d.value } })}>
                  <option value="DAILY">Every day</option>
                  <option value="DAYS">On certain days</option>
                  <option value="WEEKLY">Some times a week</option>
                </Select>
              </Field>
              {edit.f.frequency === "WEEKLY" && (
                <Field label="Times per week">
                  <Input type="number" min="1" max="7" value={edit.f.target} onChange={(_, d) => setEdit({ ...edit, f: { ...edit.f, target: d.value } })} />
                </Field>
              )}
            </div>
            {edit.f.frequency === "DAYS" && (
              <div className={px.checkRow} role="group" aria-label="Days">
                {WEEKDAY_CODES.map((c) => (
                  <Checkbox
                    key={c}
                    label={nice(c)}
                    checked={edit.f.days.includes(c)}
                    onChange={(_, d) => setEdit({ ...edit, f: { ...edit.f, days: d.checked === true ? [...edit.f.days, c] : edit.f.days.filter((x) => x !== c) } })}
                  />
                ))}
              </div>
            )}
            <div className={styles.formRow}>
              <Field label="Starts" className={styles.grow}>
                <Input type="date" value={edit.f.start} onChange={(_, d) => setEdit({ ...edit, f: { ...edit.f, start: d.value } })} />
              </Field>
              <Field label="Ends" className={styles.grow}>
                <Input type="date" value={edit.f.end} onChange={(_, d) => setEdit({ ...edit, f: { ...edit.f, end: d.value } })} />
              </Field>
              <Field label="Reminder">
                <Input type="time" value={edit.f.reminder} onChange={(_, d) => setEdit({ ...edit, f: { ...edit.f, reminder: d.value } })} />
              </Field>
            </div>
            <Field label="Colour">
              <div className={px.swatches} role="radiogroup" aria-label="Colour">
                {LIST_COLORS.map((c) => (
                  <button key={c} type="button" role="radio" aria-checked={edit.f.color === c} aria-label={c} className={`${px.swatch} ${edit.f.color === c ? px.swatchOn : ""}`} style={{ background: c }} onClick={() => setEdit({ ...edit, f: { ...edit.f, color: c } })} />
                ))}
              </div>
            </Field>
            {edit.id !== null && (
              <Switch
                checked={habits.find((h) => h.id === edit.id)?.isArchived ?? false}
                label="Archived (hidden from Today)"
                onChange={async (_, d) => {
                  if (await act(() => crud.update(PERS.habit, edit.id as number, { isArchived: d.checked }))) {
                    all.reload();
                    due.reload();
                  }
                }}
              />
            )}
          </>
        )}
      </FormDialog>

      <FormDialog
        open={logFor !== null}
        title={logFor ? `Log “${logFor.name}”` : "Log"}
        action="Save check-in"
        onClose={() => setLogFor(null)}
        onSubmit={async () => {
          if (!logFor) return;
          if (await act(() => api("POST", `/api/personal/habits/${logFor.id}/check-in`, { date: log.date, value: Math.max(0, Number(log.value) || 1), note: log.note.trim() || null, toggle: false }), "Checked in.")) {
            setLogFor(null);
            reloadAll();
          }
        }}
      >
        <div className={styles.formRow}>
          <Field label="Date" className={styles.grow}>
            <Input type="date" max={addDaysISO(today(), 1)} value={log.date} onChange={(_, d) => setLog({ ...log, date: d.value })} />
          </Field>
          <Field label="Amount">
            <Input type="number" min="0" value={log.value} onChange={(_, d) => setLog({ ...log, value: d.value })} />
          </Field>
        </div>
        <Field label="Note">
          <Input value={log.note} onChange={(_, d) => setLog({ ...log, note: d.value })} />
        </Field>
      </FormDialog>
    </>
  );
};

/* =================================================================== *
 *  Focus
 * =================================================================== */
interface FocusSession {
  id: number;
  taskId?: number | null;
  taskTitle?: string | null;
  startedAt: string;
  endedAt?: string | null;
  plannedMinutes: number;
  actualMinutes?: number | null;
  kind: string;
  status: string;
  breaksTaken: number;
  distractionCount: number;
  muteNotifications: boolean;
  notes?: string | null;
}
interface FocusSettings {
  pomodoroMinutes: number;
  shortBreakMinutes: number;
  longBreakMinutes: number;
  longBreakEvery: number;
  muteNotifications: boolean;
  autoStartBreaks: boolean;
}
interface FocusStats {
  days: number;
  sessions: number;
  totalMinutes: number;
  abandoned: number;
  distractions: number;
  avgSessionMinutes: number;
  byDay: { date: string; minutes: number; sessions: number; distractions: number }[];
  topTasks: { taskId: number; title?: string | null; minutes: number }[];
}
interface StopOut {
  sessionId: number;
  status: string;
  actualMinutes: number;
  timeEntryId?: number | null;
  nextBreak?: { kind: string; minutes: number; autoStart: boolean } | null;
}

const BarChart: React.FC<{ data: { label: string; value: number }[]; unit: string; color?: string }> = ({ data, unit, color }) => {
  if (data.length === 0) return <div className={styles.emptyRow}>No data for this period</div>;
  const W = 560;
  const H = 150;
  const P = 24;
  const max = Math.max(1, ...data.map((d) => d.value));
  const bw = (W - P * 2) / data.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={px.chart} role="img" aria-label={`Bar chart, ${unit} per day`}>
      <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#d1d1d1" />
      {data.map((d, i) => {
        const h = (d.value / max) * (H - P * 2);
        return (
          <g key={d.label + i}>
            <rect x={P + i * bw + bw * 0.15} y={H - P - h} width={bw * 0.7} height={Math.max(h, 1)} rx={3} fill={color ?? "#0f6cbd"}>
              <title>{`${d.label}: ${d.value} ${unit}`}</title>
            </rect>
            {data.length <= 16 && (
              <text x={P + i * bw + bw / 2} y={H - 8} fontSize="10" fill="#616161" textAnchor="middle">
                {d.label}
              </text>
            )}
          </g>
        );
      })}
      <text x={P} y={12} fontSize="11" fill="#616161">
        {max} {unit}
      </text>
    </svg>
  );
};

const BreakTimer: React.FC<{ kind: string; minutes: number; autoStart: boolean; onClose: () => void }> = ({ kind, minutes, autoStart, onClose }) => {
  const [left, setLeft] = useState(minutes * 60);
  const [run, setRun] = useState(autoStart);
  useEffect(() => {
    if (!run) return;
    const id = window.setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => window.clearInterval(id);
  }, [run]);
  return (
    <div className={px.card} role="timer" aria-live="off">
      <h3 className={px.cardTitle}>{kind === "LONG" ? "Long break" : "Short break"}</h3>
      <div className={px.bigNum}>{left === 0 ? "Break over" : secs(left)}</div>
      <div className={px.actions}>
        {left > 0 && (
          <Button icon={run ? <Pause20Regular /> : <Play20Regular />} onClick={() => setRun(!run)}>
            {run ? "Pause" : "Start break"}
          </Button>
        )}
        <Button onClick={onClose}>{left === 0 ? "Done" : "Skip break"}</Button>
      </div>
    </div>
  );
};

export const FocusPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [now, setNow] = useState(() => Date.now());
  const [kind, setKind] = useState("POMODORO");
  const [minutes, setMinutes] = useState("");
  const [taskId, setTaskId] = useState("");
  const [stopOpen, setStopOpen] = useState(false);
  const [stop, setStop] = useState({ completed: true, logTime: false, notes: "" });
  const [brk, setBrk] = useState<{ kind: string; minutes: number; autoStart: boolean } | null>(null);
  const [statDays, setStatDays] = useState("14");
  const [set, setSet] = useState<FocusSettings | null>(null);
  const warned = useRef<number | null>(null);

  const cur = useLoad(() => api<{ session: FocusSession | null; remainingSeconds: number | null }>("GET", "/api/personal/focus/current"), []);
  const settings = useLoad(() => api<FocusSettings>("GET", "/api/personal/focus/settings"), []);
  const next = useLoad(async () => asList<MineRow>(await api("GET", "/api/personal/focus/next")), []);
  const open = useLoad(async () => asList<MineRow>(await api("GET", "/api/tasks/my", undefined, { filter: "open", pageSize: 200 })), []);
  const stats = useLoad(() => api<FocusStats>("GET", "/api/personal/focus/stats", undefined, { days: Number(statDays) }), [statDays]);
  const history = useLoad(async () => (await crud.list<FocusSession>(PERS.session, { sort: "-startedAt", pageSize: 15 })).items, []);
  const s = cur.data?.session ?? null;

  useEffect(() => {
    if (settings.data) setSet(settings.data);
  }, [settings.data]);
  useEffect(() => {
    if (!s) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [s?.id]);

  const remaining = s ? Math.round(s.plannedMinutes * 60 - (now - utc(s.startedAt).getTime()) / 1000) : 0;
  useEffect(() => {
    if (s && remaining <= 0 && warned.current !== s.id) {
      warned.current = s.id;
      ctx.notify("info", "Time is up. Stop the session when you are ready.");
    }
  }, [s, remaining, ctx]);

  const refresh = () => {
    cur.reload();
    stats.reload();
    history.reload();
    next.reload();
  };
  const start = async (tid: string) => {
    const ok = await act(() =>
      api("POST", "/api/personal/focus/start", {
        taskId: tid ? Number(tid) : null,
        kind,
        minutes: kind === "CUSTOM" || minutes ? Math.max(1, Number(minutes) || settings.data?.pomodoroMinutes || 25) : null,
      }),
    );
    if (ok) {
      setBrk(null);
      setNow(Date.now());
      refresh();
    }
  };
  const doStop = async () => {
    try {
      const r = await api<StopOut>("POST", "/api/personal/focus/stop", { completed: stop.completed, logTime: stop.logTime, notes: stop.notes.trim() || null });
      setStopOpen(false);
      ctx.notify("success", `Session ${r.status === "COMPLETED" ? "finished" : "abandoned"}: ${r.actualMinutes} min${r.timeEntryId ? ", logged as time on the task" : ""}.`);
      setBrk(r.nextBreak ?? null);
      refresh();
    } catch (e) {
      ctx.notify("error", msgOf(e));
    }
  };

  return (
    <>
      <Head icon={<Timer20Regular />} title="Focus" sub="Work in focused blocks, count your distractions, and take breaks on purpose." />
      <Page>
        {cur.error && <Wait loading={false} error={cur.error} onRetry={cur.reload} />}
        {s ? (
          <div className={px.card} role="timer" aria-label="Focus session">
            <div className={px.rowBetween}>
              <div>
                <div className={px.bigNum}>{remaining >= 0 ? secs(remaining) : `+${secs(-remaining)}`}</div>
                <div className={px.muted}>
                  {s.taskTitle ? `On “${s.taskTitle}”` : "No task"} · {s.kind === "POMODORO" ? "Pomodoro" : "Custom"} · {s.plannedMinutes} min planned
                </div>
              </div>
              <div className={px.actions}>
                <Button
                  icon={<Alert20Regular />}
                  onClick={async () => {
                    if (await act(() => api("POST", "/api/personal/focus/distraction"))) cur.reload();
                  }}
                >
                  Distracted ({s.distractionCount})
                </Button>
                <Button
                  icon={<Pause20Regular />}
                  onClick={async () => {
                    if (await act(() => api("POST", "/api/personal/focus/break"))) cur.reload();
                  }}
                >
                  Took a break ({s.breaksTaken})
                </Button>
                <Button
                  className={styles.primary}
                  appearance="primary"
                  icon={<Stop20Regular />}
                  onClick={() => {
                    setStop({ completed: true, logTime: !!s.taskId, notes: "" });
                    setStopOpen(true);
                  }}
                >
                  Stop
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className={px.card}>
            <h3 className={px.cardTitle}>Start a session</h3>
            <div className={px.formGrid}>
              <Field label="Task">
                <Select value={taskId} onChange={(_, d) => setTaskId(d.value)}>
                  <option value="">No task</option>
                  {(open.data ?? []).map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.title}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Type">
                <Select value={kind} onChange={(_, d) => setKind(d.value)}>
                  <option value="POMODORO">Pomodoro</option>
                  <option value="CUSTOM">Custom length</option>
                </Select>
              </Field>
              <Field label="Minutes" hint={kind === "POMODORO" ? `Empty = ${settings.data?.pomodoroMinutes ?? 25}` : ""}>
                <Input type="number" min="1" max="480" value={minutes} onChange={(_, d) => setMinutes(d.value)} />
              </Field>
            </div>
            <div className={px.actions}>
              <Button className={styles.primary} appearance="primary" icon={<Play20Regular />} onClick={() => void start(taskId)}>
                Start focusing
              </Button>
            </div>
          </div>
        )}

        {brk && <BreakTimer kind={brk.kind} minutes={brk.minutes} autoStart={brk.autoStart} onClose={() => setBrk(null)} />}

        {!s && (next.data ?? []).length > 0 && (
          <div className={px.card}>
            <h3 className={px.cardTitle}>Worth doing next</h3>
            {(next.data ?? []).map((t) => (
              <div key={t.id} className={px.line}>
                <button type="button" className={px.lineMain} onClick={() => ctx.openTask(t.id)}>
                  <span className={px.lineTitle}>{t.title}</span>
                  <span className={px.lineMeta}>
                    {t.projectName ? `${t.projectName} · ` : ""}
                    {t.dueDate ? fmtDate(t.dueDate) : "No date"}
                    {t.estimateMinutes ? ` · ${fmtMin(t.estimateMinutes)}` : ""}
                  </span>
                </button>
                {t.priority !== "NONE" && <Prio p={prioFromApi(t.priority)} />}
                <Button size="small" icon={<Play20Regular />} onClick={() => void start(String(t.id))}>
                  Focus on this
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className={px.card}>
          <div className={px.rowBetween}>
            <h3 className={px.cardTitle}>Your focus time</h3>
            <Select aria-label="Period" value={statDays} onChange={(_, d) => setStatDays(d.value)}>
              {["7", "14", "30", "90"].map((d) => (
                <option key={d} value={d}>
                  Last {d} days
                </option>
              ))}
            </Select>
          </div>
          <Wait loading={stats.loading && !stats.data} error={stats.error} onRetry={stats.reload} />
          {stats.data && (
            <>
              <div className={px.tiles}>
                <Tile n={stats.data.sessions} label="Sessions" />
                <Tile n={fmtMin(stats.data.totalMinutes)} label="Focused" />
                <Tile n={`${stats.data.avgSessionMinutes} min`} label="Average session" />
                <Tile n={stats.data.distractions} label="Distractions" />
                <Tile n={stats.data.abandoned} label="Abandoned" />
              </div>
              <BarChart data={stats.data.byDay.map((d) => ({ label: fmtDate(d.date).slice(0, 6), value: d.minutes }))} unit="min" />
              {stats.data.topTasks.length > 0 && (
                <table className={px.tbl}>
                  <thead>
                    <tr>
                      <th>Most focused tasks</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.data.topTasks.map((t) => (
                      <tr key={t.taskId} className={px.clickRow} onClick={() => ctx.openTask(t.taskId)}>
                        <td className={px.strong}>{t.title ?? `Task ${t.taskId}`}</td>
                        <td>{fmtMin(t.minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        <div className={px.card}>
          <h3 className={px.cardTitle}>Focus settings</h3>
          <Wait loading={settings.loading && !set} error={settings.error} onRetry={settings.reload} />
          {set && (
            <>
              <div className={px.formGrid}>
                <Field label="Pomodoro (minutes)">
                  <Input type="number" min="5" max="180" value={String(set.pomodoroMinutes)} onChange={(_, d) => setSet({ ...set, pomodoroMinutes: Number(d.value) })} />
                </Field>
                <Field label="Short break (minutes)">
                  <Input type="number" min="1" max="60" value={String(set.shortBreakMinutes)} onChange={(_, d) => setSet({ ...set, shortBreakMinutes: Number(d.value) })} />
                </Field>
                <Field label="Long break (minutes)">
                  <Input type="number" min="1" max="120" value={String(set.longBreakMinutes)} onChange={(_, d) => setSet({ ...set, longBreakMinutes: Number(d.value) })} />
                </Field>
                <Field label="Long break after every">
                  <Input type="number" min="2" max="12" value={String(set.longBreakEvery)} onChange={(_, d) => setSet({ ...set, longBreakEvery: Number(d.value) })} />
                </Field>
              </div>
              <Switch checked={set.muteNotifications} onChange={(_, d) => setSet({ ...set, muteNotifications: d.checked })} label="Mute notifications while focusing" />
              <Switch checked={set.autoStartBreaks} onChange={(_, d) => setSet({ ...set, autoStartBreaks: d.checked })} label="Start breaks automatically" />
              <div className={px.actions}>
                <Button
                  className={styles.primary}
                  appearance="primary"
                  onClick={async () => {
                    if (await act(() => api("PUT", "/api/personal/focus/settings", set), "Settings saved.")) settings.reload();
                  }}
                >
                  Save settings
                </Button>
              </div>
            </>
          )}
        </div>

        <div className={px.card}>
          <h3 className={px.cardTitle}>Recent sessions</h3>
          <Wait loading={history.loading && !history.data} error={history.error} empty={!history.loading && (history.data ?? []).length === 0 && "No sessions yet"} onRetry={history.reload} />
          {(history.data ?? []).length > 0 && (
            <div className={px.scroll}>
              <table className={px.tbl}>
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Type</th>
                    <th>Length</th>
                    <th>Status</th>
                    <th>Distractions</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(history.data ?? []).map((h) => (
                    <tr key={h.id}>
                      <td>{fmtStamp(h.startedAt)}</td>
                      <td>{nice(h.kind)}</td>
                      <td>{h.actualMinutes ?? h.plannedMinutes} min</td>
                      <td>
                        <Chip text={h.status} />
                      </td>
                      <td>{h.distractionCount}</td>
                      <td>
                        {h.status !== "RUNNING" && (
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<Delete20Regular />}
                            aria-label="Delete session"
                            onClick={async () => {
                              if (await act(() => crud.remove(PERS.session, h.id))) refresh();
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Page>

      <FormDialog open={stopOpen} title="Stop this session" action={stop.completed ? "Finish session" : "Abandon session"} onClose={() => setStopOpen(false)} onSubmit={() => void doStop()}>
        <Switch checked={stop.completed} onChange={(_, d) => setStop({ ...stop, completed: d.checked })} label="I finished this block" />
        <Checkbox checked={stop.logTime} disabled={!s?.taskId} onChange={(_, d) => setStop({ ...stop, logTime: d.checked === true })} label={s?.taskId ? "Log this time on the task" : "Log time (needs a task)"} />
        <Field label="Notes">
          <Textarea value={stop.notes} onChange={(_, d) => setStop({ ...stop, notes: d.value })} rows={2} resize="vertical" />
        </Field>
      </FormDialog>
    </>
  );
};

/* =================================================================== *
 *  Priority matrix: Eisenhower, value / effort, MoSCoW
 * =================================================================== */
interface MatrixTask {
  id: number;
  title: string;
  priority: string;
  dueDate?: string | null;
  quadrant?: string | null;
  estimateMinutes?: number | null;
}
interface MatrixOut {
  quadrants: { quadrant: string; tasks: MatrixTask[] }[];
  unclassified: { id: number; title: string; priority: string; dueDate?: string | null; suggested: string }[];
}
interface Coaching {
  counts: { q1: number; q2: number; q3: number; q4: number; unclassified: number };
  messages: { key: string; text: string }[];
}
interface VeItem {
  taskId: number;
  title: string;
  value?: number | null;
  effort?: number | null;
  bucket: string;
  score?: number | null;
}
interface MoscowCol {
  moscow: string;
  items: { taskId: number; title: string; priority: string; dueDate?: string | null }[];
}
const QUAD: Record<string, { name: string; sub: string }> = {
  Q1: { name: "Do first", sub: "Urgent and important" },
  Q2: { name: "Schedule", sub: "Important, not urgent" },
  Q3: { name: "Delegate", sub: "Urgent, not important" },
  Q4: { name: "Drop", sub: "Neither" },
};
const BUCKETS: Record<string, { name: string; sub: string }> = {
  QUICK_WIN: { name: "Quick wins", sub: "High value, low effort" },
  BIG_BET: { name: "Big bets", sub: "High value, high effort" },
  FILL_IN: { name: "Fill-ins", sub: "Low value, low effort" },
  MONEY_PIT: { name: "Money pits", sub: "Low value, high effort" },
};
const MOSCOW_NAME: Record<string, string> = { MUST: "Must have", SHOULD: "Should have", COULD: "Could have", WONT: "Won’t have" };

export const MatrixPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("eisenhower");
  return (
    <>
      <Head icon={<Apps20Regular />} title="Priorities" sub="Decide what matters: Eisenhower matrix, value against effort, and MoSCoW." />
      <div className={styles.bar}>
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(String(d.value))}>
          <Tab value="eisenhower">Urgent / Important</Tab>
          <Tab value="value">Value / Effort</Tab>
          <Tab value="moscow">MoSCoW</Tab>
        </TabList>
      </div>
      {tab === "eisenhower" && <EisenhowerTab ctx={ctx} />}
      {tab === "value" && <ValueEffortTab ctx={ctx} />}
      {tab === "moscow" && <MoscowTab ctx={ctx} />}
    </>
  );
};

const EisenhowerTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [drag, setDrag] = useState<number | null>(null);
  const m = useLoad(() => api<MatrixOut>("GET", "/api/personal/matrix"), []);
  const c = useLoad(() => api<Coaching>("GET", "/api/personal/matrix/coaching"), []);
  const reload = () => {
    m.reload();
    c.reload();
  };
  const classify = async (id: number, q: string) => {
    if (await act(() => api("POST", `/api/personal/matrix/${id}/classify`, { quadrant: q }))) reload();
  };
  const quads = m.data?.quadrants ?? [];
  const un = m.data?.unclassified ?? [];
  return (
    <Page>
      <div className={px.rowBetween}>
        <p className={px.hint}>Drag a task into a box, or use the menu on the task. Only your own open tasks are shown.</p>
        <Button
          icon={<Sparkle20Regular />}
          disabled={un.length === 0}
          onClick={async () => {
            try {
              const r = await api<{ classified: number; skipped: number }>("POST", "/api/personal/matrix/auto-classify");
              ctx.notify("success", `${r.classified} sorted${r.skipped ? `, ${r.skipped} skipped (no edit access)` : ""}.`);
              reload();
            } catch (e) {
              ctx.notify("error", msgOf(e));
            }
          }}
        >
          Sort the rest automatically
        </Button>
      </div>
      <Wait loading={m.loading && !m.data} error={m.error} onRetry={m.reload} />
      {m.data && (
        <div className={px.matrix}>
          {["Q1", "Q2", "Q3", "Q4"].map((q) => {
            const list = quads.find((x) => x.quadrant === q)?.tasks ?? [];
            return (
              <section
                key={q}
                className={`${px.quad} ${px["quad" + q]}`}
                aria-label={QUAD[q]?.name}
                onDragOver={(e) => drag !== null && e.preventDefault()}
                onDrop={() => {
                  if (drag !== null) void classify(drag, q);
                  setDrag(null);
                }}
              >
                <header className={px.quadHead}>
                  <strong>{QUAD[q]?.name}</strong>
                  <span className={px.muted}>{QUAD[q]?.sub}</span>
                  <span className={px.count}>{list.length}</span>
                </header>
                {list.map((t) => (
                  <div key={t.id} className={px.mCard} draggable onDragStart={() => setDrag(t.id)} onDragEnd={() => setDrag(null)}>
                    <button type="button" className={px.lineMain} onClick={() => ctx.openTask(t.id)}>
                      <span className={px.lineTitle}>{t.title}</span>
                      <span className={px.lineMeta}>
                        {t.dueDate ? fmtDate(t.dueDate) : "No date"}
                        {t.estimateMinutes ? ` · ${fmtMin(t.estimateMinutes)}` : ""}
                      </span>
                    </button>
                    <Select size="small" aria-label={`Move ${t.title}`} value={q} onChange={(_, d) => d.value !== q && void classify(t.id, d.value)}>
                      {["Q1", "Q2", "Q3", "Q4"].map((x) => (
                        <option key={x} value={x}>
                          {QUAD[x]?.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                ))}
                {list.length === 0 && <div className={px.muted}>Drop tasks here</div>}
              </section>
            );
          })}
        </div>
      )}
      {un.length > 0 && (
        <div className={px.card}>
          <h3 className={px.cardTitle}>
            Not sorted yet <span className={px.count}>{un.length}</span>
          </h3>
          {un.map((t) => (
            <div key={t.id} className={px.line} draggable onDragStart={() => setDrag(t.id)} onDragEnd={() => setDrag(null)}>
              <button type="button" className={px.lineMain} onClick={() => ctx.openTask(t.id)}>
                <span className={px.lineTitle}>{t.title}</span>
                <span className={px.lineMeta}>
                  {t.dueDate ? fmtDate(t.dueDate) : "No date"} · suggested: {QUAD[t.suggested]?.name ?? t.suggested}
                </span>
              </button>
              {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                <Button key={q} size="small" appearance={q === t.suggested ? "primary" : "secondary"} className={q === t.suggested ? styles.primary : undefined} onClick={() => void classify(t.id, q)}>
                  {QUAD[q]?.name}
                </Button>
              ))}
            </div>
          ))}
        </div>
      )}
      {c.data && (
        <div className={px.card}>
          <h3 className={px.cardTitle}>
            <Lightbulb20Regular /> Coaching
          </h3>
          {c.data.messages.map((x) => (
            <p key={x.key} className={px.coach}>
              {x.text}
            </p>
          ))}
        </div>
      )}
    </Page>
  );
};

/** create or update the score row of one task in one framework */
const useScores = (ctx: PageCtx, framework: "VALUE_EFFORT" | "MOSCOW") => {
  const act = useAct(ctx.notify);
  const save = useCallback(
    async (taskId: number, patch: Record<string, unknown>) =>
      act(async () => {
        const found = (await crud.list<{ id: number }>(PERS.score, { workspaceId: ctx.workspaceId, taskId, framework })).items[0];
        if (found) await crud.update(PERS.score, found.id, patch);
        else await crud.create(PERS.score, { workspaceId: ctx.workspaceId, taskId, framework, ...patch });
      }),
    [act, ctx.workspaceId, framework],
  );
  const remove = useCallback(
    async (taskId: number) =>
      act(async () => {
        const found = (await crud.list<{ id: number }>(PERS.score, { workspaceId: ctx.workspaceId, taskId, framework })).items[0];
        if (found) await crud.remove(PERS.score, found.id);
      }, "Removed from the board."),
    [act, ctx.workspaceId, framework],
  );
  return { save, remove };
};

const OpenTaskSelect: React.FC<{ ctx: PageCtx; exclude: number[]; value: string; onChange: (v: string) => void }> = ({ ctx, exclude, value, onChange }) => {
  const open = useLoad(async () => asList<MineRow>(await api("GET", "/api/tasks/my", undefined, { filter: "open", workspaceId: ctx.workspaceId, pageSize: 200 })), [ctx.workspaceId]);
  return (
    <Select aria-label="Task" value={value} onChange={(_, d) => onChange(d.value)}>
      <option value="">Choose one of your open tasks…</option>
      {(open.data ?? [])
        .filter((t) => !exclude.includes(t.id))
        .map((t) => (
          <option key={t.id} value={String(t.id)}>
            {t.title}
          </option>
        ))}
    </Select>
  );
};

const ValueEffortTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const sc = useScores(ctx, "VALUE_EFFORT");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ taskId: "", value: "3", effort: "3" });
  const l = useLoad(() => api<{ items: VeItem[] }>("GET", "/api/personal/frameworks/value-effort", undefined, { workspaceId: ctx.workspaceId }), [ctx.workspaceId]);
  const items = l.data?.items ?? [];
  return (
    <Page>
      <div className={px.rowBetween}>
        <p className={px.hint}>Score each task from 1 to 5 for value and for effort. High value with low effort comes first.</p>
        <Button
          className={styles.primary}
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => {
            setF({ taskId: "", value: "3", effort: "3" });
            setOpen(true);
          }}
        >
          Score a task
        </Button>
      </div>
      <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && items.length === 0 && "No tasks scored yet"} onRetry={l.reload} />
      {items.length > 0 && (
        <>
          <div className={px.ve}>
            {Object.keys(BUCKETS).map((b) => (
              <section key={b} className={px.quad} aria-label={BUCKETS[b]?.name}>
                <header className={px.quadHead}>
                  <strong>{BUCKETS[b]?.name}</strong>
                  <span className={px.muted}>{BUCKETS[b]?.sub}</span>
                  <span className={px.count}>{items.filter((i) => i.bucket === b).length}</span>
                </header>
                {items
                  .filter((i) => i.bucket === b)
                  .map((i) => (
                    <button key={i.taskId} type="button" className={px.mCard} onClick={() => ctx.openTask(i.taskId)}>
                      <span className={px.lineTitle}>{i.title}</span>
                      <span className={px.lineMeta}>
                        Value {i.value} · Effort {i.effort}
                      </span>
                    </button>
                  ))}
              </section>
            ))}
          </div>
          <div className={px.scroll}>
            <table className={px.tbl}>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Value</th>
                  <th>Effort</th>
                  <th>Score</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.taskId}>
                    <td className={px.strong}>{i.title}</td>
                    <td>
                      <Select size="small" aria-label={`Value of ${i.title}`} value={String(i.value ?? 3)} onChange={async (_, d) => (await sc.save(i.taskId, { value: Number(d.value) })) && l.reload()}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <option key={n}>{n}</option>
                        ))}
                      </Select>
                    </td>
                    <td>
                      <Select size="small" aria-label={`Effort of ${i.title}`} value={String(i.effort ?? 3)} onChange={async (_, d) => (await sc.save(i.taskId, { effort: Number(d.value) })) && l.reload()}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <option key={n}>{n}</option>
                        ))}
                      </Select>
                    </td>
                    <td>{i.score ?? "—"}</td>
                    <td>
                      <Button size="small" appearance="subtle" icon={<Delete20Regular />} aria-label={`Remove ${i.title}`} onClick={async () => (await sc.remove(i.taskId)) && l.reload()} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <FormDialog
        open={open}
        title="Score a task"
        action="Save score"
        disabled={!f.taskId}
        onClose={() => setOpen(false)}
        onSubmit={async () => {
          if (await sc.save(Number(f.taskId), { value: Number(f.value), effort: Number(f.effort) })) {
            setOpen(false);
            l.reload();
          }
        }}
      >
        <Field label="Task" required>
          <OpenTaskSelect ctx={ctx} exclude={items.map((i) => i.taskId)} value={f.taskId} onChange={(v) => setF({ ...f, taskId: v })} />
        </Field>
        <div className={styles.formRow}>
          <Field label="Value (1–5)" className={styles.grow}>
            <Select value={f.value} onChange={(_, d) => setF({ ...f, value: d.value })}>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </Select>
          </Field>
          <Field label="Effort (1–5)" className={styles.grow}>
            <Select value={f.effort} onChange={(_, d) => setF({ ...f, effort: d.value })}>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </Select>
          </Field>
        </div>
      </FormDialog>
    </Page>
  );
};

const MoscowTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const sc = useScores(ctx, "MOSCOW");
  const [drag, setDrag] = useState<number | null>(null);
  const [add, setAdd] = useState({ taskId: "", cls: "MUST" });
  const l = useLoad(async () => asList<MoscowCol>(await api("GET", "/api/personal/frameworks/moscow", undefined, { workspaceId: ctx.workspaceId })), [ctx.workspaceId]);
  const cols = l.data ?? [];
  const used = cols.flatMap((c) => c.items.map((i) => i.taskId));
  const set = async (taskId: number, cls: string) => {
    if (await sc.save(taskId, { moscowClass: cls })) l.reload();
  };
  return (
    <Page>
      <p className={px.hint}>Drag a task between columns, or use the menu on it.</p>
      <div className={px.addRow}>
        <div className={px.grow}>
          <OpenTaskSelect ctx={ctx} exclude={used} value={add.taskId} onChange={(v) => setAdd({ ...add, taskId: v })} />
        </div>
        <Select aria-label="Class" value={add.cls} onChange={(_, d) => setAdd({ ...add, cls: d.value })}>
          {Object.keys(MOSCOW_NAME).map((k) => (
            <option key={k} value={k}>
              {MOSCOW_NAME[k]}
            </option>
          ))}
        </Select>
        <Button
          className={styles.primary}
          appearance="primary"
          icon={<Add20Regular />}
          disabled={!add.taskId}
          onClick={async () => {
            if (await sc.save(Number(add.taskId), { moscowClass: add.cls })) {
              setAdd({ ...add, taskId: "" });
              l.reload();
            }
          }}
        >
          Add
        </Button>
      </div>
      <Wait loading={l.loading && !l.data} error={l.error} onRetry={l.reload} />
      {l.data && (
        <div className={px.moscow}>
          {Object.keys(MOSCOW_NAME).map((k) => {
            const col = cols.find((c) => c.moscow === k);
            const list = col?.items ?? [];
            return (
              <section
                key={k}
                className={px.quad}
                aria-label={MOSCOW_NAME[k]}
                onDragOver={(e) => drag !== null && e.preventDefault()}
                onDrop={() => {
                  if (drag !== null) void set(drag, k);
                  setDrag(null);
                }}
              >
                <header className={px.quadHead}>
                  <strong>{MOSCOW_NAME[k]}</strong>
                  <span className={px.count}>{list.length}</span>
                </header>
                {list.map((i) => (
                  <div key={i.taskId} className={px.mCard} draggable onDragStart={() => setDrag(i.taskId)} onDragEnd={() => setDrag(null)}>
                    <button type="button" className={px.lineMain} onClick={() => ctx.openTask(i.taskId)}>
                      <span className={px.lineTitle}>{i.title}</span>
                      <span className={px.lineMeta}>{i.dueDate ? fmtDate(i.dueDate) : "No date"}</span>
                    </button>
                    <Select size="small" aria-label={`Move ${i.title}`} value={k} onChange={(_, d) => d.value !== k && void set(i.taskId, d.value)}>
                      {Object.keys(MOSCOW_NAME).map((x) => (
                        <option key={x} value={x}>
                          {MOSCOW_NAME[x]}
                        </option>
                      ))}
                    </Select>
                    <Button size="small" appearance="subtle" icon={<Dismiss16Regular />} aria-label={`Remove ${i.title}`} onClick={async () => (await sc.remove(i.taskId)) && l.reload()} />
                  </div>
                ))}
                {list.length === 0 && <div className={px.muted}>Nothing here</div>}
              </section>
            );
          })}
        </div>
      )}
    </Page>
  );
};

/* =================================================================== *
 *  Routines
 * =================================================================== */
interface RoutineStep {
  id: number;
  routineId: number;
  title: string;
  habitId?: number | null;
  durationMinutes?: number | null;
  sortOrder: number;
  doneToday?: boolean | null;
}
interface RoutineToday {
  id: number;
  name: string;
  routineType: string;
  startTime?: string | null;
  steps: RoutineStep[];
}
interface RoutineRec {
  id: number;
  name: string;
  routineType: string;
  daysOfWeek?: string | null;
  startTime?: string | null;
  isActive: boolean;
}
interface RoutineDetail extends RoutineRec {
  steps: RoutineStep[];
  totalMinutes: number;
}
interface Template {
  template: string;
  type: string;
  name: string;
  startTime: string;
  steps: { title: string; minutes: number }[];
}
/** the days column holds at most 20 characters, so “every day” is stored as empty */
const daysValue = (days: string[]) => (days.length === 0 || days.length === 7 ? null : WEEKDAY_CODES.filter((d) => days.includes(d)).join(","));

const DayPicker: React.FC<{ value: string[]; onChange: (v: string[]) => void }> = ({ value, onChange }) => (
  <div className={px.checkRow} role="group" aria-label="Days">
    {WEEKDAY_CODES.map((c) => (
      <Checkbox key={c} label={nice(c)} checked={value.includes(c)} onChange={(_, d) => onChange(d.checked === true ? [...value, c] : value.filter((x) => x !== c))} />
    ))}
  </div>
);

export const RoutinesPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [tab, setTab] = useState("today");
  const [selId, setSelId] = useState<number | null>(null);
  const [tpl, setTpl] = useState<Template | null>(null);
  const [tplF, setTplF] = useState<{ start: string; days: string[] }>({ start: "", days: [] });
  const [newOpen, setNewOpen] = useState(false);
  const [rf, setRf] = useState({ name: "", type: "CUSTOM", start: "08:00", days: [] as string[] });
  const [stepEdit, setStepEdit] = useState<{ id: number | null; title: string; habitId: string; minutes: string } | null>(null);
  const [ticked, setTicked] = useState<Record<number, boolean>>({});

  const todayR = useLoad(async () => asList<RoutineToday>(await api("GET", "/api/personal/routines/today")), []);
  const templates = useLoad(async () => asList<Template>(await api("GET", "/api/personal/routines/templates")), []);
  const mine = useLoad(async () => (await crud.list<RoutineRec>(PERS.routine, { sort: "startTime" })).items, []);
  const detail = useLoad(() => (selId === null ? Promise.resolve(null) : api<RoutineDetail>("GET", `/api/personal/routines/${selId}`)), [selId]);
  const habits = useLoad(async () => (await crud.list<{ id: number; name: string }>(PERS.habit, { isArchived: false, sort: "name" })).items, []);
  const d = detail.data;
  const reloadAll = () => {
    todayR.reload();
    mine.reload();
    detail.reload();
  };

  const createFromTemplate = async () => {
    if (!tpl) return;
    try {
      const r = await api<RoutineDetail>("POST", "/api/personal/routines/from-template", { template: tpl.template, startTime: tplF.start || null, daysOfWeek: daysValue(tplF.days) });
      setTpl(null);
      ctx.notify("success", `“${r.name}” created with ${r.steps.length} steps.`);
      setSelId(r.id);
      setTab("mine");
      reloadAll();
    } catch (e) {
      ctx.notify("error", msgOf(e));
    }
  };
  const swap = async (i: number, by: number) => {
    if (!d) return;
    const a = d.steps[i];
    const b = d.steps[i + by];
    if (!a || !b) return;
    if (await act(async () => {
      await crud.update(PERS.step, a.id, { sortOrder: b.sortOrder });
      await crud.update(PERS.step, b.id, { sortOrder: a.sortOrder });
    })) detail.reload();
  };

  return (
    <>
      <Head icon={<WeatherSunny20Regular />} title="Routines" sub="Morning, evening and work-day routines, one step at a time.">
        <Button
          className={styles.primary}
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => {
            setRf({ name: "", type: "CUSTOM", start: "08:00", days: [] });
            setNewOpen(true);
          }}
        >
          New routine
        </Button>
      </Head>
      <div className={styles.bar}>
        <TabList selectedValue={tab} onTabSelect={(_, x) => setTab(String(x.value))}>
          <Tab value="today">Today</Tab>
          <Tab value="mine">My routines</Tab>
          <Tab value="templates">Templates</Tab>
        </TabList>
      </div>
      <Page>
        {tab === "today" && (
          <>
            <Wait loading={todayR.loading && !todayR.data} error={todayR.error} empty={!todayR.loading && (todayR.data ?? []).length === 0 && "No routines today. Start from a template."} onRetry={todayR.reload} />
            {(todayR.data ?? []).map((r) => {
              const done = r.steps.filter((s) => (s.habitId ? s.doneToday === true : ticked[s.id] === true)).length;
              return (
                <div key={r.id} className={px.card}>
                  <div className={px.rowBetween}>
                    <h3 className={px.cardTitle}>
                      {r.name} {r.startTime && <span className={px.muted}>· {r.startTime}</span>}
                    </h3>
                    <span className={px.count}>
                      {done}/{r.steps.length}
                    </span>
                  </div>
                  {r.steps.map((s) => (
                    <div key={s.id} className={px.line}>
                      <Checkbox
                        checked={s.habitId ? s.doneToday === true : ticked[s.id] === true}
                        label={s.title}
                        onChange={async (_, c) => {
                          if (s.habitId) {
                            if (await act(() => api("POST", `/api/personal/habits/${s.habitId}/check-in`, { toggle: true }))) todayR.reload();
                          } else setTicked({ ...ticked, [s.id]: c.checked === true });
                        }}
                      />
                      <span className={px.grow} />
                      {s.habitId && <Chip text="Habit" />}
                      {s.durationMinutes ? <span className={px.muted}>{s.durationMinutes} min</span> : null}
                    </div>
                  ))}
                </div>
              );
            })}
          </>
        )}

        {tab === "templates" && (
          <>
            <Wait loading={templates.loading && !templates.data} error={templates.error} onRetry={templates.reload} />
            <div className={px.tplGrid}>
              {(templates.data ?? []).map((t) => (
                <div key={t.template} className={px.card}>
                  <h3 className={px.cardTitle}>{t.name}</h3>
                  <div className={px.muted}>
                    Starts {t.startTime} · {fmtMin(t.steps.reduce((a, s) => a + s.minutes, 0))}
                  </div>
                  <ol className={px.steps}>
                    {t.steps.map((s, i) => (
                      <li key={i}>
                        {s.title} <span className={px.muted}>· {s.minutes} min</span>
                      </li>
                    ))}
                  </ol>
                  <div className={px.actions}>
                    <Button
                      className={styles.primary}
                      appearance="primary"
                      onClick={() => {
                        setTplF({ start: t.startTime, days: [] });
                        setTpl(t);
                      }}
                    >
                      Use this template
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "mine" && (
          <>
            <Wait loading={mine.loading && !mine.data} error={mine.error} empty={!mine.loading && (mine.data ?? []).length === 0 && "No routines yet"} onRetry={mine.reload} />
            {(mine.data ?? []).length > 0 && (
              <div className={px.scroll}>
                <table className={px.tbl}>
                  <thead>
                    <tr>
                      <th>Routine</th>
                      <th>Type</th>
                      <th>Days</th>
                      <th>Starts</th>
                      <th>Active</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(mine.data ?? []).map((r) => (
                      <tr key={r.id} className={selId === r.id ? px.rowOn : undefined}>
                        <td className={px.strong}>{r.name}</td>
                        <td>{nice(r.routineType)}</td>
                        <td>{r.daysOfWeek ? r.daysOfWeek.replace(/,/g, ", ") : "Every day"}</td>
                        <td>{r.startTime ?? "—"}</td>
                        <td>
                          <Switch
                            checked={r.isActive}
                            aria-label={`${r.name} is ${r.isActive ? "on" : "off"}`}
                            onChange={async (_, c) => {
                              if (await act(() => crud.update(PERS.routine, r.id, { isActive: c.checked }))) reloadAll();
                            }}
                          />
                        </td>
                        <td className={px.actions}>
                          <Button size="small" onClick={() => setSelId(r.id)}>
                            Steps
                          </Button>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<Delete20Regular />}
                            aria-label={`Delete ${r.name}`}
                            onClick={async () => {
                              if (await act(() => crud.remove(PERS.routine, r.id), "Routine deleted.")) {
                                if (selId === r.id) setSelId(null);
                                reloadAll();
                              }
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {selId !== null && (
              <div className={px.card}>
                <div className={px.rowBetween}>
                  <h3 className={px.cardTitle}>{d ? `${d.name} · ${fmtMin(d.totalMinutes)}` : "Steps"}</h3>
                  <div className={px.actions}>
                    <Button icon={<Add20Regular />} onClick={() => setStepEdit({ id: null, title: "", habitId: "", minutes: "10" })}>
                      Add step
                    </Button>
                    <Button onClick={() => setSelId(null)}>Close</Button>
                  </div>
                </div>
                <Wait loading={detail.loading && !d} error={detail.error} empty={!detail.loading && d !== null && d.steps.length === 0 && "No steps yet"} onRetry={detail.reload} />
                {d?.steps.map((s, i) => (
                  <div key={s.id} className={px.planRow}>
                    <div className={px.planMove}>
                      <Button size="small" appearance="subtle" icon={<ArrowUp16Regular />} aria-label="Move up" disabled={i === 0} onClick={() => void swap(i, -1)} />
                      <Button size="small" appearance="subtle" icon={<ArrowDown16Regular />} aria-label="Move down" disabled={i === d.steps.length - 1} onClick={() => void swap(i, 1)} />
                    </div>
                    <div className={px.grow}>
                      <div className={px.lineTitle}>{s.title}</div>
                      <span className={px.lineMeta}>
                        {s.durationMinutes ? `${s.durationMinutes} min` : "No length"}
                        {s.habitId ? ` · habit: ${(habits.data ?? []).find((h) => h.id === s.habitId)?.name ?? s.habitId}` : ""}
                      </span>
                    </div>
                    <Button size="small" appearance="subtle" icon={<Edit20Regular />} aria-label={`Edit ${s.title}`} onClick={() => setStepEdit({ id: s.id, title: s.title, habitId: s.habitId ? String(s.habitId) : "", minutes: String(s.durationMinutes ?? "") })} />
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<Delete20Regular />}
                      aria-label={`Delete ${s.title}`}
                      onClick={async () => {
                        if (await act(() => crud.remove(PERS.step, s.id))) reloadAll();
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Page>

      <FormDialog open={tpl !== null} title={tpl ? tpl.name : "Template"} action="Create routine" onClose={() => setTpl(null)} onSubmit={() => void createFromTemplate()}>
        <Field label="Starts at">
          <Input type="time" value={tplF.start} onChange={(_, x) => setTplF({ ...tplF, start: x.value })} />
        </Field>
        <Field label="On which days?" hint="Leave all unticked for every day.">
          <DayPicker value={tplF.days} onChange={(v) => setTplF({ ...tplF, days: v })} />
        </Field>
      </FormDialog>

      <FormDialog
        open={newOpen}
        title="New routine"
        action="Create routine"
        disabled={!rf.name.trim()}
        onClose={() => setNewOpen(false)}
        onSubmit={async () => {
          try {
            const c = await crud.create<{ id: number }>(PERS.routine, { name: rf.name.trim(), routineType: rf.type, startTime: rf.start || null, daysOfWeek: daysValue(rf.days), isActive: true });
            setNewOpen(false);
            setSelId(c.id);
            setTab("mine");
            ctx.notify("success", "Routine created. Add its steps below.");
            reloadAll();
          } catch (e) {
            ctx.notify("error", msgOf(e));
          }
        }}
      >
        <Field label="Name" required>
          <Input value={rf.name} onChange={(_, x) => setRf({ ...rf, name: x.value })} placeholder="Shutdown ritual" />
        </Field>
        <div className={styles.formRow}>
          <Field label="Type" className={styles.grow}>
            <Select value={rf.type} onChange={(_, x) => setRf({ ...rf, type: x.value })}>
              <option value="MORNING">Morning</option>
              <option value="EVENING">Evening</option>
              <option value="CUSTOM">Custom</option>
            </Select>
          </Field>
          <Field label="Starts at">
            <Input type="time" value={rf.start} onChange={(_, x) => setRf({ ...rf, start: x.value })} />
          </Field>
        </div>
        <Field label="On which days?" hint="Leave all unticked for every day.">
          <DayPicker value={rf.days} onChange={(v) => setRf({ ...rf, days: v })} />
        </Field>
      </FormDialog>

      <FormDialog
        open={stepEdit !== null}
        title={stepEdit?.id === null ? "Add step" : "Edit step"}
        action="Save step"
        disabled={!stepEdit?.title.trim()}
        onClose={() => setStepEdit(null)}
        onSubmit={async () => {
          if (!stepEdit || !d) return;
          const body = { title: stepEdit.title.trim(), habitId: stepEdit.habitId ? Number(stepEdit.habitId) : null, durationMinutes: stepEdit.minutes ? Math.max(1, Number(stepEdit.minutes)) : null };
          const last = d.steps[d.steps.length - 1];
          if (await act(() => (stepEdit.id === null ? crud.create(PERS.step, { ...body, routineId: d.id, sortOrder: (last?.sortOrder ?? 0) + 10 }) : crud.update(PERS.step, stepEdit.id, body)))) {
            setStepEdit(null);
            reloadAll();
          }
        }}
      >
        {stepEdit && (
          <>
            <Field label="What to do" required>
              <Input value={stepEdit.title} onChange={(_, x) => setStepEdit({ ...stepEdit, title: x.value })} />
            </Field>
            <div className={styles.formRow}>
              <Field label="Minutes" className={styles.grow}>
                <Input type="number" min="1" value={stepEdit.minutes} onChange={(_, x) => setStepEdit({ ...stepEdit, minutes: x.value })} />
              </Field>
              <Field label="Counts as habit" className={styles.grow}>
                <Select value={stepEdit.habitId} onChange={(_, x) => setStepEdit({ ...stepEdit, habitId: x.value })}>
                  <option value="">None</option>
                  {(habits.data ?? []).map((h) => (
                    <option key={h.id} value={String(h.id)}>
                      {h.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </>
        )}
      </FormDialog>
    </>
  );
};